import { useState } from 'react';
import { Button, Fact, NotProvided, linkTextClasses } from '@evogard/ui';
import type { ChargeSettlement } from '@evogard/contract';
import { cents, format, formatDate, formatInstant, fromColumn } from '@evogard/domain';

/**
 * A baixa de uma cobrança paga, como o Asaas a descreveu (19/10/2026): o que
 * foi pago, o que caiu na conta, a compensação e o comprovante.
 *
 * Mora em `components/`, e não numa das pastas, porque duas telas a desenham —
 * a de cobranças do Financeiro e a mesa do acionamento de Periféricos — e uma
 * tela de módulo não importa da pasta de outro.
 *
 * São `Fact`s soltos: quem chama os põe dentro do `Facts` dele. O que o Asaas
 * não mandou aparece como não informado. O líquido vem do Asaas, e não de uma
 * conta feita aqui; a tarifa mostrada é só a diferença entre os dois valores
 * que ele mandou.
 */
export function SettlementFacts({
  settlement: s,
  openReceipt,
}: {
  settlement: ChargeSettlement;
  /** A URL assinada do PDF guardado. */
  openReceipt: () => Promise<string>;
}) {
  const paid = money(s.paidAmount);
  const net = money(s.netAmount);
  const interest = money(s.interestAmount);
  return (
    <>
      <Fact label="Pago em">
        {s.paymentDate ? formatDate(s.paymentDate) : <NotProvided />}
        {s.clientPaymentDate && s.clientPaymentDate !== s.paymentDate && (
          <span className="block text-xs font-normal text-texto-suave">
            o associado pagou em {formatDate(s.clientPaymentDate)}
          </span>
        )}
      </Fact>
      <Fact label="Valor pago">
        {paid !== null ? (
          <span className="tabular-nums">{format(paid)}</span>
        ) : (
          <NotProvided />
        )}
        {interest !== null && interest > 0 && (
          <span className="block text-xs font-normal text-texto-suave">
            com {format(interest)} de juros e multa
          </span>
        )}
      </Fact>
      <Fact label="Líquido na conta">
        {net !== null ? (
          <span className="tabular-nums">{format(net)}</span>
        ) : (
          <NotProvided />
        )}
        {net !== null && paid !== null && paid > net && (
          <span className="block text-xs font-normal text-texto-suave">
            tarifa do Asaas: {format(cents(paid - net))}
          </span>
        )}
      </Fact>
      <Fact label="Compensação">
        {s.creditDate ? (
          `Compensado em ${formatDate(s.creditDate)}`
        ) : s.estimatedCreditDate ? (
          `Prevista para ${formatDate(s.estimatedCreditDate)}`
        ) : (
          <NotProvided />
        )}
      </Fact>
      {s.cardBrand || s.cardLastDigits ? (
        <Fact label="Cartão">
          {[s.cardBrand, s.cardLastDigits && `final ${s.cardLastDigits}`]
            .filter(Boolean)
            .join(' · ')}
        </Fact>
      ) : null}
      {s.pixTransactionId && (
        <Fact label="Transação Pix no Asaas">
          <span className="font-mono text-xs">{s.pixTransactionId}</span>
        </Fact>
      )}
      {s.nossoNumero && <Fact label="Nosso número">{s.nossoNumero}</Fact>}
      <Fact label="Fatura no Asaas">{s.invoiceNumber ?? <NotProvided />}</Fact>
      <Fact label="Comprovante" wide>
        <Receipt settlement={s} openReceipt={openReceipt} />
      </Fact>
    </>
  );
}

function Receipt({
  settlement: s,
  openReceipt,
}: {
  settlement: ChargeSettlement;
  openReceipt: () => Promise<string>;
}) {
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (s.receiptStored) {
    return (
      <span className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="contorno"
          icon="baixar"
          loading={opening}
          onClick={() => {
            setOpening(true);
            setError(null);
            openReceipt()
              .then((url) => window.open(url, '_blank', 'noopener'))
              .catch((e: Error) => setError(e.message))
              .finally(() => setOpening(false));
          }}
        >
          Baixar comprovante (PDF)
        </Button>
        {s.receiptStoredAt && (
          <span className="text-xs font-normal text-texto-suave">
            guardado em {formatInstant(s.receiptStoredAt)}
          </span>
        )}
        {error && (
          <span role="alert" className="text-xs text-erro">
            {error}
          </span>
        )}
      </span>
    );
  }
  if (s.receiptUrl) {
    return (
      <span className="font-normal">
        O PDF ainda não foi guardado — a casa tenta de novo a cada 15 minutos.{' '}
        <a
          href={s.receiptUrl}
          target="_blank"
          rel="noreferrer"
          className={linkTextClasses()}
        >
          Ver no Asaas
        </a>
      </span>
    );
  }
  return (
    <span className="font-normal text-texto-suave">O Asaas não mandou comprovante.</span>
  );
}

function money(v: string | null) {
  return v === null ? null : fromColumn(v);
}
